import { useState } from 'react'

import { ActionIcon } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { v1 as uuid } from 'uuid'
import { z } from 'zod'

import IconChevronDown from 'src/icons/IconChevronDown'
import IconTrash from 'src/icons/IconTrash'
import { formatAddress } from 'src/lib/address.utils'

import { getUtcMonth } from '../utils'

import { ACCIDENTS_CARD_ANCHOR_ID } from './AiFlagsPill/AiFlagsPill'
import PlacesAutocomplete from './AddressInput/PlacesAutocomplete'
import ApplicationCard from './ApplicationCard/ApplicationCard'
import MonthInput from './MonthInput/MonthInput'
import MvrFindingsPill from './MvrFindingsPill/MvrFindingsPill'
import PspFindingsPill from './PspFindingsPill/PspFindingsPill'
import TextAreaInput from './TextareaInput/TextareaInput'

const yesNo = (v?: boolean) => (v ? 'Yes' : 'No')

type AccidentFormValue = {
  accidentId?: string
  accidentDate?: Date
  city: string
  state: string
  country: string
  description: string
  isPreventable: boolean
  isCommercialMotorVehicleInvolved: boolean
  involvedFatalities: boolean
  involvedInjuries: boolean
}

// A single label/value segmented pill (e.g. "CMV | Yes"). Values are rendered
// in neutral gray — accident answers are not color-coded. The value half is a
// fixed width (not min-width) so "Yes" and "No" occupy exactly the same
// column width — otherwise content-driven Yes pills sit ~4px wider than No
// pills, compounding visibly across the four pills per row (ENG-4253).
const FactSegment = ({ label, value }: { label: string; value: string }) => (
  <span className="inline-flex items-stretch overflow-hidden rounded border border-doubleNickel-gray-200 text-xs leading-none">
    <span className="bg-doubleNickel-gray-50 px-1.5 py-1 text-doubleNickel-gray-500">
      {label}
    </span>
    <span className="w-9 border-l border-doubleNickel-gray-200 bg-doubleNickel-white px-1.5 py-1 text-center text-doubleNickel-gray-800">
      {value}
    </span>
  </span>
)

// Neutral Yes/No segmented control: a grey track with the selected option
// raised as a white pill (the design system's "active" treatment).
const SegmentedToggle = ({
  value,
  onChange,
}: {
  value: boolean
  onChange: (v: boolean) => void
}) => (
  <div className="inline-flex gap-0.5 rounded border border-doubleNickel-gray-200 bg-doubleNickel-gray-100 p-0.5 text-[11px]">
    {[true, false].map((opt) => {
      const selected = opt === value
      return (
        <button
          type="button"
          key={String(opt)}
          onClick={() => onChange(opt)}
          className={`rounded-[3px] px-2.5 py-[3px] ${
            selected
              ? 'bg-doubleNickel-white font-semibold text-doubleNickel-gray-900 shadow-sm'
              : 'font-medium text-doubleNickel-gray-500'
          }`}
        >
          {opt ? 'Yes' : 'No'}
        </button>
      )
    })}
  </div>
)

const AccidentsCard = ({
  values,
  handleUpdateApplication,
  applicantId = undefined,
  readOnly = false,
}) => {
  const [editing, setEditing] = useState(false)
  // Read view: which timeline nodes are expanded (keyed by id).
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Edit view (variation B): which event is open in the focused panel.
  const [activeIndex, setActiveIndex] = useState(0)

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const defaultAccident = {
    accidentId: uuid(),
    accidentDate: undefined,
    city: '',
    state: '',
    country: '',
    description: '',
    isPreventable: false,
    isCommercialMotorVehicleInvolved: false,
    involvedFatalities: false,
    involvedInjuries: false,
  }

  const schema = z.object({
    accidents: z.array(
      z.object({
        accidentId: z.string().optional(),
        accidentDate: z.date().optional(),
        city: z.string().min(1).max(100),
        state: z.string().min(2).max(50),
        description: z.string().min(1).max(1000),
        isPreventable: z.boolean(),
        isCommercialMotorVehicleInvolved: z.boolean(),
        involvedFatalities: z.boolean(),
        involvedInjuries: z.boolean(),
      })
    ),
  })

  // Reverse-chronological (newest → oldest) — most recent accident first
  // matches how recruiters scan the record. Undated accidents (incomplete
  // data) sort to the bottom rather than to the top, even though the
  // direction is reversed — they're "unknown date" not "newest". Used at
  // form initialization AND on save so that adding a new accident or editing
  // an existing accident's date can't leave the timeline out of order after
  // the user transitions back to read mode.
  const sortReverseChronological = (
    accidents: AccidentFormValue[]
  ): AccidentFormValue[] =>
    [...accidents].sort((a, b) => {
      const aTime = a.accidentDate?.getTime()
      const bTime = b.accidentDate?.getTime()
      // Undated rows always last regardless of direction.
      if (aTime == null && bTime == null) return 0
      if (aTime == null) return 1
      if (bTime == null) return -1
      return bTime - aTime
    })

  const mapToFormValues = (): AccidentFormValue[] =>
    sortReverseChronological(
      values.map((accident) => ({
        ...(accident?.accidentId && { accidentId: accident.accidentId }),
        accidentDate: accident?.accidentDate
          ? new Date(accident.accidentDate)
          : undefined,
        city: accident?.city || '',
        state: accident?.state || '',
        country: accident?.country || 'USA',
        description: accident?.description || '',
        isPreventable: accident?.isPreventable || false,
        isCommercialMotorVehicleInvolved:
          accident?.isCommercialMotorVehicleInvolved || false,
        involvedFatalities: accident?.involvedFatalities || false,
        involvedInjuries: accident?.involvedInjuries || false,
      }))
    )

  const form = useForm<{ accidents: AccidentFormValue[] }>({
    initialValues: {
      accidents: mapToFormValues(),
    },
    validate: zodResolver(schema),
  })

  // Wraps setEditing so entering edit focuses the first event.
  const handleSetEditing = (val: boolean) => {
    if (val) setActiveIndex(0)
    setEditing(val)
  }

  const handleAdd = () => {
    const newIndex = form.values.accidents.length
    setEditing(true)
    form.insertListItem('accidents', defaultAccident)
    setActiveIndex(newIndex)
  }

  const handleCancel = () => {
    form.setValues({ accidents: mapToFormValues() })
    setEditing(false)
  }

  const handleDelete = (index) => {
    form.removeListItem(`accidents`, index)
    setActiveIndex((current) => {
      if (index === current) return Math.max(0, index - 1)
      if (index < current) return current - 1
      return current
    })
  }

  const handleSave = () => {
    // Sort once and reuse: the backend payload and the form's local state
    // both need to reflect the chronological order. Without committing the
    // sort to form state, read mode after save would still render whatever
    // order edits left the form in (e.g., new accident appended last, or a
    // re-dated accident sitting at its old index).
    const sorted = sortReverseChronological(form.values.accidents)
    const updates = {
      accidents: sorted.map((accident) => {
        const { accidentId, ...rest } = accident
        return rest
      }),
    }
    handleUpdateApplication(updates).then((res) => {
      if (res && !res.errors) {
        form.setValues({ accidents: sorted })
        setEditing(false)
      }
    })
  }

  const accidents = form.values.accidents
  // Keep the focused index in range as events are added/removed.
  const activeIdx = Math.min(activeIndex, accidents.length - 1)

  return (
    <ApplicationCard
      id={ACCIDENTS_CARD_ANCHOR_ID}
      title={'Accidents & Violations'}
      titleExtra={
        applicantId && (
          <div className="flex items-center gap-2">
            <MvrFindingsPill
              applicantId={applicantId}
              disclosedAccidentCount={values.length}
            />
            <PspFindingsPill
              applicantId={applicantId}
              disclosedAccidentCount={values.length}
            />

          </div>
        )
      }
      editing={editing}
      isEmpty={accidents.length === 0}
      setEditing={handleSetEditing}
      isSaveDisabled={!form.isValid()}
      handleSave={handleSave}
      handleCancel={handleCancel}
      handleAdd={handleAdd}
      readonly={readOnly}
    >
      {/* Timeline: a vertical spine threads each accident node. */}
      <div className="relative pl-6">
        <div className="absolute bottom-2 left-[5px] top-2 w-px bg-doubleNickel-gray-200" />

        {accidents.map((value, index) => {
          const key = value.accidentId || `idx-${index}`
          const isOpen = expanded.has(key)
          // Country is intentionally omitted — accidents are domestic, so the
          // "USA" suffix is noise. City + state is enough to place the event.
          const location = formatAddress({ ...value, country: undefined })
          const facts = [
            {
              label: 'CMV',
              value: yesNo(value.isCommercialMotorVehicleInvolved),
            },
            {
              label: 'Fatal',
              value: yesNo(value.involvedFatalities),
            },
            {
              label: 'Injury',
              value: yesNo(value.involvedInjuries),
            },
            { label: 'Preventable', value: yesNo(value.isPreventable) },
          ]

          // ---- Edit mode (variation B) ----
          if (editing) {
            // Non-focused events stay as dimmed read rows; click to focus.
            if (index !== activeIdx) {
              return (
                <div key={key} className="relative pb-[18px] last:pb-0">
                  <span className="absolute -left-[23px] top-1.5 z-[1] h-[11px] w-[11px] rounded-full border-2 border-doubleNickel-gray-300 bg-doubleNickel-white" />
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className="flex w-full items-center gap-2 rounded px-1.5 py-2 text-left opacity-60 hover:bg-doubleNickel-gray-50 hover:opacity-100"
                  >
                    <span
                      className="w-[140px] min-w-0 flex-none truncate text-doubleNickel-gray-900"
                      style={{ fontSize: 13, fontWeight: 600 }}
                      title={location || 'Unknown location'}
                    >
                      {location || 'Unknown location'}
                    </span>
                    <span className="flex flex-wrap items-center gap-1.5">
                      {facts.map((f) => (
                        <FactSegment key={f.label} {...f} />
                      ))}
                    </span>
                    <span
                      className="ml-auto whitespace-nowrap text-xs text-doubleNickel-gray-700"
                      style={{ fontFamily: 'monospace' }}
                    >
                      {getUtcMonth(value.accidentDate, '—')}
                    </span>
                  </button>
                </div>
              )
            }

            // Focused event: a white panel with inline inputs + toggles.
            const factToggles = [
              { label: 'CMV', field: 'isCommercialMotorVehicleInvolved' },
              { label: 'Fatalities', field: 'involvedFatalities' },
              { label: 'Injuries', field: 'involvedInjuries' },
              { label: 'Preventable', field: 'isPreventable' },
            ]
            return (
              <div key={key} className="relative pb-[18px] last:pb-0">
                <span className="absolute -left-[23px] top-1.5 z-[1] h-[11px] w-[11px] rounded-full border-2 border-doubleNickel-gray-700 bg-doubleNickel-gray-700" />
                <div className="rounded-lg border border-doubleNickel-gray-300 bg-doubleNickel-white p-3.5 shadow-sm">
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-8">
                      <PlacesAutocomplete
                        label="Location"
                        required={editing}
                        placeholder="Select a city from the dropdown"
                        value={value}
                        types={['(cities)']}
                        // 12px input text — matches the Description field and the
                        // fact pills / Employment position-name scale on this card.
                        classNames={{ input: 'text-xs' }}
                        handleChange={(v) => {
                          form.setFieldValue(`accidents.${index}.city`, v.city)
                          form.setFieldValue(`accidents.${index}.state`, v.state)
                          form.setFieldValue(
                            `accidents.${index}.country`,
                            v.country
                          )
                        }}
                      />
                    </div>
                    <div className="col-span-4">
                      <MonthInput
                        editing={editing}
                        label="Event Date"
                        placeholder="MM/YYYY"
                        required={editing}
                        value={value.accidentDate}
                        maxDate={new Date()}
                        handleChange={(v) => {
                          form.setFieldValue(
                            `accidents.${index}.accidentDate`,
                            v
                          )
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-3">
                    {factToggles.map((t) => (
                      <div key={t.field} className="flex flex-col gap-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-doubleNickel-gray-400">
                          {t.label}
                        </span>
                        <SegmentedToggle
                          value={!!value[t.field]}
                          onChange={(v) =>
                            form.setFieldValue(`accidents.${index}.${t.field}`, v)
                          }
                        />
                      </div>
                    ))}
                  </div>

                  <div className="mt-3">
                    <TextAreaInput
                      label="Description"
                      editing={editing}
                      required={editing}
                      value={value.description}
                      handleChange={(v) => {
                        form.setFieldValue(`accidents.${index}.description`, v)
                      }}
                      minRows={3}
                      // 12px input text to match the Location field and the fact
                      // pills. Overrides TextAreaInput's default text-sm; safe
                      // because this instance always renders in edit mode
                      // (caret-inherit), so no dynamic caret class is lost.
                      classNames={{
                        label: 'text-sm font-medium text-doubleNickel-gray-700',
                        input:
                          'cursor-pointer whitespace-pre-wrap text-xs text-doubleNickel-gray-900 caret-inherit [&[data-disabled]]:bg-doubleNickel-white [&[data-disabled]]:opacity-100 [&[data-disabled]]:text-doubleNickel-gray-600',
                      }}
                    />
                  </div>

                  <div className="mt-3.5 flex justify-end border-t border-doubleNickel-gray-200 pt-3">
                    <ActionIcon
                      className="h-8 w-8 border border-doubleNickel-gray-200 bg-doubleNickel-white shadow-sm hover:bg-doubleNickel-error-25"
                      onClick={() => handleDelete(index)}
                    >
                      <IconTrash className="h-4 w-4 fill-none stroke-doubleNickel-error-500" />
                    </ActionIcon>
                  </div>
                </div>
              </div>
            )
          }

          // ---- Read mode: collapsed summary, expands to detail ----
          return (
            <div key={key} className="relative pb-[18px] last:pb-0">
              <span className="absolute -left-[23px] top-1.5 z-[1] h-[11px] w-[11px] rounded-full border-2 border-doubleNickel-gray-300 bg-doubleNickel-white" />
              <button
                type="button"
                onClick={() => toggleExpanded(key)}
                className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-doubleNickel-gray-50"
                aria-expanded={isOpen}
              >
                <span
                  className="w-[140px] min-w-0 flex-none truncate text-doubleNickel-gray-900"
                  style={{ fontSize: 13, fontWeight: 600 }}
                  title={location || 'Unknown location'}
                >
                  {location || 'Unknown location'}
                </span>
                <span className="flex flex-wrap items-center gap-1.5">
                  {facts.map((f) => (
                    <FactSegment key={f.label} {...f} />
                  ))}
                </span>
                <span
                  className="ml-auto whitespace-nowrap text-xs text-doubleNickel-gray-700"
                  style={{ fontFamily: 'monospace' }}
                >
                  {getUtcMonth(value.accidentDate, '—')}
                </span>
                <IconChevronDown
                  className={`h-3.5 w-3.5 flex-none fill-none stroke-doubleNickel-gray-400 transition-transform ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isOpen && (
                <div className="flex flex-wrap gap-7 px-1.5 pb-1 pt-2">
                  <div>
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-doubleNickel-gray-400">
                      Description
                    </div>
                    <div className="text-xs text-doubleNickel-gray-800">
                      {value.description || '—'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </ApplicationCard>
  )
}

export default AccidentsCard
